/**
 * Report API
 * Handles report-related API endpoints
 */

import { executeRequest } from './hooks/useApiQuery';

export type SuggestedResource = {
  type?: string;
  title?: string;
  link: string;
  thumbnailUrl?: string;
  _id?: string;
};

export interface ResourceServiceStatus {
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  data?: {
    resources?: SuggestedResource[];
  };
  error?: string;
}

export interface InterviewDetails {
  id?: string;
  user_name?: string;
  user_email?: string;
  interview_status?: string;
  duration?: number;
  thread_id?: string;
  rating?: number;
  type?: string;
  version?: string;
  role_interview_info?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReportStatusResponse {
  interviewId: string;
  generationStatus: 'pending' | 'generating' | 'completed' | 'failed';
  coreReport: {
    status: string;
    data?: Record<string, unknown>;
    error?: string;
  };
  feedback: {
    status: string;
    data?: Record<string, unknown>;
    error?: string;
  };
  resources?: ResourceServiceStatus;
  interviewDetails?: InterviewDetails | null;
  interviewThreadId?: string | null;
  interviewMessages?: Array<{
    id: string;
    message: string;
    role: string;
    index: number;
    timestamp: string;
    isInstruction?: boolean;
    createdAt?: string;
    updatedAt?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface VideoStatusResponse {
  interviewId: string;
  isMerged: boolean;
  mergedUrl: string | null;
  statusOfRecording: 'in_progress' | 'completed' | null;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Get report generation status
 */
export async function getReportStatus(interviewId: string): Promise<ReportStatusResponse> {
  const response = await executeRequest<ApiResponse<ReportStatusResponse>>({
    url: `/report/status/${interviewId}`,
    method: 'GET',
  });
  return response.data;
}

/**
 * Trigger report generation
 */
export async function generateReport(interviewId: string): Promise<{ status: string; message: string }> {
  const response = await executeRequest<ApiResponse<{ status: string; message: string }>>({
    url: `/report/generate/${interviewId}`,
    method: 'POST',
  });
  return response.data;
}

/**
 * Get video processing status
 */
export async function getVideoStatus(interviewId: string): Promise<VideoStatusResponse> {
  const response = await executeRequest<ApiResponse<VideoStatusResponse>>({
    url: `/report/video-status/${interviewId}`,
    method: 'GET',
  });
  return response.data;
}
